const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('token');
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
      ...options,
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // Auth endpoints
  async login(credentials) {
    return this.request('/auth/login', {
      method: 'POST',
      body: credentials,
    });
  }

  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: userData,
    });
  }

  async getProfile() {
    return this.request('/auth/profile', {
      method: 'GET',
    });
  }

  // Work Orders endpoints
  async getWorkOrders() {
    return this.request('/work-orders', {
      method: 'GET',
    });
  }

  async createWorkOrder(workOrder) {
    return this.request('/work-orders', {
      method: 'POST',
      body: workOrder,
    });
  }

  async updateWorkOrder(id, workOrder) {
    return this.request(`/work-orders/${id}`, {
      method: 'PATCH',
      body: workOrder,
    });
  }

  async deleteWorkOrder(id) {
    return this.request(`/work-orders/${id}`, {
      method: 'DELETE',
    });
  }

  // Analytics endpoints
  async getAnalytics() {
    return this.request('/analytics', {
      method: 'GET',
    });
  }

  async createAnalytics(analytics) {
    return this.request('/analytics', {
      method: 'POST',
      body: analytics,
    });
  }

  // Shop Floor endpoints
  async getShopFloorWorkOrders() {
    return this.request('/shopfloor/workorders', {
      method: 'GET',
    });
  }


  async loginOperator(credentials) {
    return this.request('/shopfloor/operators/login', {
      method: 'POST',
      body: credentials,
    });
  }

  async recordProductionEvent(event) {
    return this.request('/shopfloor/production/events', {
      method: 'POST',
      body: event,
    });
  }

  // Master Data endpoints
  async getMasterData(type) {
    return this.request(`/master-data/${type}`, {
      method: 'GET',
    });
  }

  async createMasterData(type, data) {
    return this.request(`/master-data/${type}`, {
      method: 'POST',
      body: data,
    });
  }

  async updateMasterData(type, id, data) {
    return this.request(`/master-data/${type}/${id}`, {
      method: 'PUT',
      body: data,
    });
  }

  async deleteMasterData(type, id) {
    return this.request(`/master-data/${type}/${id}`, {
      method: 'DELETE',
    });
  }

  // Specific master data endpoints
  async getItems() {
    return this.request('/master-data/items', {
      method: 'GET',
    });
  }

  async createItem(item) {
    return this.request('/master-data/items', {
      method: 'POST',
      body: item,
    });
  }

  async updateItem(id, item) {
    return this.request(`/master-data/items/${id}`, {
      method: 'PUT',
      body: item,
    });
  }

  async deleteItem(id) {
    return this.request(`/master-data/items/${id}`, {
      method: 'DELETE',
    });
  }

  async getWorkCenters() {
    return this.request('/master-data/work-centers', {
      method: 'GET',
    });
  }

  async createWorkCenter(workCenter) {
    return this.request('/master-data/work-centers', {
      method: 'POST',
      body: workCenter,
    });
  }

  async updateWorkCenter(id, workCenter) {
    return this.request(`/master-data/work-centers/${id}`, {
      method: 'PUT',
      body: workCenter,
    });
  }

  async deleteWorkCenter(id) {
    return this.request(`/master-data/work-centers/${id}`, {
      method: 'DELETE',
    });
  }

  async getLocations() {
    return this.request('/master-data/locations', {
      method: 'GET',
    });
  }

  async createLocation(location) {
    return this.request('/master-data/locations', {
      method: 'POST',
      body: location,
    });
  }

  async updateLocation(id, location) {
    return this.request(`/master-data/locations/${id}`, {
      method: 'PUT',
      body: location,
    });
  }

  async deleteLocation(id) {
    return this.request(`/master-data/locations/${id}`, {
      method: 'DELETE',
    });
  }

  // BOM endpoints
  async getBOMs() {
    return this.request('/master-data/bill-of-materials', {
      method: 'GET',
    });
  }

  async createBOM(bom) {
    return this.request('/master-data/bill-of-materials', {
      method: 'POST',
      body: bom,
    });
  }

  async updateBOM(id, bom) {
    return this.request(`/master-data/bill-of-materials/${id}`, {
      method: 'PUT',
      body: bom,
    });
  }

  async deleteBOM(id) {
    return this.request(`/master-data/bill-of-materials/${id}`, {
      method: 'DELETE',
    });
  }

  // Planning endpoints
  async getPlanning() {
    return this.request('/planning', {
      method: 'GET',
    });
  }

  async getPlanningKPIs() {
    return this.request('/planning/kpis', {
      method: 'GET',
    });
  }

  async getMasterProductionSchedule() {
    return this.request('/planning/mps', {
      method: 'GET',
    });
  }

  async updateMasterProductionSchedule(mpsData) {
    return this.request('/planning/mps', {
      method: 'PUT',
      body: mpsData,
    });
  }

  async runMRP() {
    return this.request('/planning/mrp/run', {
      method: 'POST',
    });
  }

  async getMRPResults() {
    return this.request('/planning/mrp/results', {
      method: 'GET',
    });
  }

  async generateCRP() {
    return this.request('/planning/crp/generate', {
      method: 'POST',
    });
  }

  async getCapacityRequirements() {
    return this.request('/planning/crp', {
      method: 'GET',
    });
  }

  async getDemandForecast() {
    return this.request('/planning/forecast', {
      method: 'GET',
    });
  }

  async updateDemandForecast(forecastData) {
    return this.request('/planning/forecast', {
      method: 'PUT',
      body: forecastData,
    });
  }

  async getPlanningAlerts() {
    return this.request('/planning/alerts', {
      method: 'GET',
    });
  }

  async getPlanningTimeline() {
    return this.request('/planning/timeline', {
      method: 'GET',
    });
  }

  async importForecastData(data) {
    return this.request('/planning/forecast/import', {
      method: 'POST',
      body: data,
    });
  }

  async exportPlanningData(params) {
    return this.request('/planning/export', {
      method: 'POST',
      body: params,
    });
  }

  // Maintenance endpoints
  async getMaintenance() {
    return this.request('/maintenance', {
      method: 'GET',
    });
  }

  // Quality Control Plans endpoints (via Master Data Service)
  async getControlPlans() {
    return this.request('/master-data/control-plans', {
      method: 'GET',
    });
  }

  async getControlPlan(id) {
    return this.request(`/master-data/control-plans/${id}`, {
      method: 'GET',
    });
  }

  async createControlPlan(controlPlan) {
    return this.request('/master-data/control-plans', {
      method: 'POST',
      body: controlPlan,
    });
  }

  async updateControlPlan(id, controlPlan) {
    return this.request(`/master-data/control-plans/${id}`, {
      method: 'PUT',
      body: controlPlan,
    });
  }

  async deleteControlPlan(id) {
    return this.request(`/master-data/control-plans/${id}`, {
      method: 'DELETE',
    });
  }

  // Sampling Plans endpoints (via Master Data Service)
  async getSamplingPlans() {
    return this.request('/master-data/sampling-plans', {
      method: 'GET',
    });
  }

  async getSamplingPlan(id) {
    return this.request(`/master-data/sampling-plans/${id}`, {
      method: 'GET',
    });
  }

  async createSamplingPlan(samplingPlan) {
    return this.request('/master-data/sampling-plans', {
      method: 'POST',
      body: samplingPlan,
    });
  }

  async updateSamplingPlan(id, samplingPlan) {
    return this.request(`/master-data/sampling-plans/${id}`, {
      method: 'PUT',
      body: samplingPlan,
    });
  }

  async deleteSamplingPlan(id) {
    return this.request(`/master-data/sampling-plans/${id}`, {
      method: 'DELETE',
    });
  }

  // Quality Inspections endpoints
  async getQualityInspections() {
    return this.request('/quality/quality-checks', {
      method: 'GET',
    });
  }

  async createQualityInspection(inspection) {
    return this.request('/quality/quality-checks', {
      method: 'POST',
      body: inspection,
    });
  }

  async updateQualityInspection(id, inspection) {
    return this.request(`/quality/quality-checks/${id}`, {
      method: 'PUT',
      body: inspection,
    });
  }

  // Defect Tracking endpoints
  async getDefects() {
    return this.request('/quality/defects', {
      method: 'GET',
    });
  }

  async getDefect(id) {
    return this.request(`/quality/defects/${id}`, {
      method: 'GET',
    });
  }

  async createDefect(defect) {
    return this.request('/quality/defects', {
      method: 'POST',
      body: defect,
    });
  }

  async updateDefect(id, defect) {
    return this.request(`/quality/defects/${id}`, {
      method: 'PUT',
      body: defect,
    });
  }

  async deleteDefect(id) {
    return this.request(`/quality/defects/${id}`, {
      method: 'DELETE',
    });
  }

  async getDefectAnalytics() {
    return this.request('/quality/defects/analytics', {
      method: 'GET',
    });
  }

  // Defect Types endpoints
  async getDefectTypes() {
    return this.request('/quality/defect-types', {
      method: 'GET',
    });
  }

  async createDefectType(defectType) {
    return this.request('/quality/defect-types', {
      method: 'POST',
      body: defectType,
    });
  }

  async updateDefectType(id, defectType) {
    return this.request(`/quality/defect-types/${id}`, {
      method: 'PUT',
      body: defectType,
    });
  }

  async deleteDefectType(id) {
    return this.request(`/quality/defect-types/${id}`, {
      method: 'DELETE',
    });
  }
}

export default new ApiService();